import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BiCoin } from 'react-icons/bi';
import { FiCheck, FiTrendingUp } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useGetPlanInfoQuery, useGetSessionProvideListQuery, useProviderPaymentPayModMutation } from '@/app/services/billing-rtk.service';
import LoadingComponent from '@/components/common/loading/loading.component';
import ApiFailure from '@/components/ui/ApiFailure';
import LouiseButton from '@/components/ui/LouiseButton';
import { MultiLanguageUrl } from '@/constants/multiLanguage.constant';
import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';

import { EPlanCodes, getHighestValidPlan, planLevelsArray } from './billing-create';

type PlanTier = 'FREE' | 'BASIC' | 'ADVANCED' | 'ENTERPRISE';

interface PlanPricing {
  plan: PlanTier;
  planCodes: string[];
  products: { price: number; sessionAmount: number }[];
}

const PLAN_ORDER: PlanTier[] = ['FREE', 'BASIC', 'ADVANCED', 'ENTERPRISE'];

const PLAN_DISPLAY_NAMES: Record<PlanTier, string> = {
  FREE: 'Free',
  BASIC: 'Basic',
  ADVANCED: 'Advanced',
  ENTERPRISE: 'Enterprise',
};

const PLAN_COLORS: Record<PlanTier, { bg: string; text: string; border: string; badge: string; gradient: string }> = {
  FREE: { bg: 'tw-bg-gray-50', text: 'tw-text-gray-600', border: 'tw-border-gray-200', badge: 'tw-bg-gray-100', gradient: 'tw-from-gray-400 tw-to-gray-600' },
  BASIC: { bg: 'tw-bg-blue-50', text: 'tw-text-blue-600', border: 'tw-border-blue-200', badge: 'tw-bg-blue-100', gradient: 'tw-from-blue-400 tw-to-blue-600' },
  ADVANCED: {
    bg: 'tw-bg-purple-50',
    text: 'tw-text-purple-600',
    border: 'tw-border-purple-200',
    badge: 'tw-bg-purple-100',
    gradient: 'tw-from-purple-400 tw-to-purple-600',
  },
  ENTERPRISE: {
    bg: 'tw-bg-amber-50',
    text: 'tw-text-amber-600',
    border: 'tw-border-amber-200',
    badge: 'tw-bg-amber-100',
    gradient: 'tw-from-amber-400 tw-to-amber-600',
  },
};

// Popular package amounts for highlighting (1000 is the target - center stage effect)
const POPULAR_AMOUNTS = [1000];

const CURRENCY = 'USD';

// Helper to get plan tier from plan code
const getPlanTierFromCode = (planCode: string): PlanTier => {
  if (
    planCode.includes('ENTERPRISE') ||
    planCode.includes('8F83AHUURU') ||
    planCode.includes('TX5B3V1J9V') ||
    planCode.includes('5Q5FNDRELE') ||
    planCode.includes('Y8O3FH61GW')
  ) {
    return 'ENTERPRISE';
  }
  if (planCode.includes('ADVANCED') || planCode.includes('OSMFCYDT0V') || planCode.includes('B42CDBJP56')) {
    return 'ADVANCED';
  }
  if (planCode.includes('BASIC') || planCode.includes('YXN1LILRWY') || planCode.includes('02OW2E8C4U')) {
    return 'BASIC';
  }
  return 'FREE';
};

// Session Card Component
interface SessionCardProps {
  sessionAmount: number;
  price: number;
  isSelected: boolean;
  isPopular: boolean;
  onClick: () => void;
  t: (key: string) => string;
}

const SessionCard: React.FC<SessionCardProps> = ({ sessionAmount, price, isSelected, isPopular, onClick, t }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        tw-relative tw-flex tw-flex-col tw-items-center tw-p-4 tw-rounded-xl tw-border-2 tw-transition-all tw-duration-200
        ${
          isSelected
            ? 'tw-border-blue-500 tw-bg-blue-50 tw-shadow-lg tw-scale-[1.02]'
            : 'tw-border-gray-200 tw-bg-white hover:tw-border-blue-300 hover:tw-shadow-md'
        }
        tw-cursor-pointer tw-w-full
      `}
    >
      {/* Badge */}
      {isPopular && (
        <div className="tw-absolute -tw-top-3 tw-left-1/2 tw-transform -tw-translate-x-1/2 tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-font-bold tw-text-white tw-bg-blue-600 tw-whitespace-nowrap">
          {t('AddCreditPage/popular')}
        </div>
      )}

      {/* Session Amount */}
      <div className="tw-flex tw-items-center tw-gap-2 tw-mb-1">
        <BiCoin className="tw-text-amber-500 tw-hidden sm:tw-block" size={28} />
        <span className="tw-text-3xl tw-font-bold tw-text-gray-900 tw-font-['Lexend']">{sessionAmount.toLocaleString()}</span>
      </div>

      <span className="tw-text-sm tw-text-gray-500 tw-font-['Lexend'] tw-mb-3">{t('AddCreditPage/l-05')}</span>

      {/* Price */}
      <div className="tw-flex tw-items-baseline tw-gap-1">
        <span className="tw-text-xl tw-font-semibold tw-text-gray-700 tw-font-['Lexend']">${price}</span>
      </div>

      {/* Selected indicator */}
      {isSelected && (
        <div className="tw-absolute tw-top-2 tw-right-2 tw-w-6 tw-h-6 tw-bg-blue-500 tw-rounded-full tw-flex tw-items-center tw-justify-center">
          <FiCheck className="tw-text-white" size={14} />
        </div>
      )}
    </button>
  );
};

const CreditCreatePage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [selectedPackage, setSelectedPackage] = useState<{ price: number; sessionAmount: number } | null>(null);

  const getPlanInfo = useGetPlanInfoQuery();
  const { data, isLoading, refetch, isSuccess, isError } = useGetSessionProvideListQuery();
  const [mutate, { isLoading: isPaymentLoading }] = useProviderPaymentPayModMutation();

  const highestPlan = useMemo(() => {
    return (
      getHighestValidPlan(getPlanInfo.data, planLevelsArray) || {
        name: EPlanCodes.FREE,
        level: 1,
      }
    );
  }, [getPlanInfo.data]);

  const currentPlanTier = useMemo(() => {
    return getPlanTierFromCode(highestPlan.name);
  }, [highestPlan]);

  const sessionPackages = useMemo(() => {
    if (!data || !highestPlan) return [];
    return data.find((x: PlanPricing) => x.planCodes.includes(highestPlan.name))?.products || [];
  }, [data, highestPlan]);

  // Get pricing for all plans to show comparison
  const allPlanPricing = useMemo(() => {
    if (!data) return [];
    return data as PlanPricing[];
  }, [data]);

  // Calculate savings comparison for the selected session amount
  const savingsComparison = useMemo(() => {
    if (!selectedPackage || !allPlanPricing.length) return null;

    const currentPlanIndex = PLAN_ORDER.indexOf(currentPlanTier);
    const higherPlans = PLAN_ORDER.slice(currentPlanIndex + 1);

    if (higherPlans.length === 0) return null;

    const currentPlanData = allPlanPricing.find(p => p.plan === currentPlanTier);
    const currentPrice = currentPlanData?.products.find(p => p.sessionAmount === selectedPackage.sessionAmount)?.price;

    if (!currentPrice) return null;

    return higherPlans
      .map(planTier => {
        const planData = allPlanPricing.find(p => p.plan === planTier);
        const planPrice = planData?.products.find(p => p.sessionAmount === selectedPackage.sessionAmount)?.price;

        if (!planPrice) return null;

        const savings = currentPrice - planPrice;
        const savingsPercent = Math.round((savings / currentPrice) * 100);

        return {
          plan: planTier,
          price: planPrice,
          savings,
          savingsPercent,
        };
      })
      .filter(Boolean) as { plan: PlanTier; price: number; savings: number; savingsPercent: number }[];
  }, [selectedPackage, allPlanPricing, currentPlanTier]);

  const handlePackageSelect = (pkg: { price: number; sessionAmount: number }) => {
    setSelectedPackage(pkg);
  };

  const handlePurchase = () => {
    if (!selectedPackage) {
      toast.error(t('AddCreditPage/select-package-error'));
      return;
    }

    mutate({
      redirectUrl: `${MultiLanguageUrl[i18n.language] || 'en-US'}/control-panel/setting`,
      amount: selectedPackage.price,
      currency: CURRENCY,
      provider: 'louisebot_session',
    })
      .unwrap()
      .then(res => {
        return window.open(res, '_self');
      })
      .catch(err => {
        toast.error(err.data.message || err.message);
      });
  };

  const handleUpgradeClick = () => {
    navigate('../billing-create');
  };

  let content;

  if (getPlanInfo.isLoading || isLoading) content = <LoadingComponent />;

  if (getPlanInfo.isError || isError)
    content = (
      <ApiFailure
        refetch={() => {
          getPlanInfo.refetch();
          refetch();
        }}
      />
    );

  if (getPlanInfo.isSuccess || isSuccess)
    content = (
      <section className="tw-min-h-screen tw-bg-gradient-to-br tw-from-gray-50 tw-to-blue-50 tw-py-6 tw-px-4">
        <div className="tw-max-w-6xl tw-mx-auto">
          {/* Header Section */}
          <div className="tw-text-center tw-mb-8">
            <div className="tw-inline-flex tw-items-center tw-gap-2 tw-bg-white tw-px-4 tw-py-2 tw-rounded-full tw-shadow-sm tw-mb-4">
              <BiCoin className="tw-text-amber-500" size={20} />
              <span className="tw-text-sm tw-font-medium tw-text-gray-600 tw-font-['Lexend']">
                {t('AddCreditPage/current-plan')}:
                <span className={`tw-ml-1 tw-font-semibold ${PLAN_COLORS[currentPlanTier].text}`}>{PLAN_DISPLAY_NAMES[currentPlanTier]}</span>
              </span>
            </div>
            <h1 className="tw-text-3xl md:tw-text-4xl tw-font-bold tw-text-gray-900 tw-font-['Lexend'] tw-mb-3">{t('AddCreditPage/t-02')}</h1>
          </div>

          {/* Session Packages Grid */}
          <div className="tw-bg-white tw-rounded-2xl tw-shadow-xl tw-p-6 md:tw-p-8 tw-mb-8">
            <div className="tw-flex tw-items-center tw-gap-3 tw-mb-6">
              <div className="tw-w-10 tw-h-10 tw-bg-amber-100 tw-rounded-xl tw-flex tw-items-center tw-justify-center">
                <BiCoin className="tw-text-amber-600" size={24} />
              </div>
              <div>
                <h2 className="tw-text-xl tw-font-bold tw-text-gray-900 tw-font-['Lexend'] tw-m-0">{t('AddCreditPage/select-package')}</h2>
              </div>
            </div>

            <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-5 tw-gap-4">
              {sessionPackages.map((pkg: { price: number; sessionAmount: number }) => (
                <SessionCard
                  key={pkg.sessionAmount}
                  sessionAmount={pkg.sessionAmount}
                  price={pkg.price}
                  isSelected={selectedPackage?.sessionAmount === pkg.sessionAmount}
                  isPopular={POPULAR_AMOUNTS.includes(pkg.sessionAmount)}
                  onClick={() => handlePackageSelect(pkg)}
                  t={t}
                />
              ))}
            </div>
          </div>

          {/* Price Comparison Section - Shows when a package is selected */}
          {selectedPackage && savingsComparison && savingsComparison.length > 0 && (
            <div className="tw-bg-white tw-rounded-2xl tw-shadow-xl tw-p-6 md:tw-p-8 tw-mb-8 tw-border tw-border-green-100">
              <div className="tw-flex tw-items-center tw-gap-3 tw-mb-6">
                <div className="tw-w-10 tw-h-10 tw-bg-green-100 tw-rounded-xl tw-flex tw-items-center tw-justify-center">
                  <FiTrendingUp className="tw-text-green-600" size={24} />
                </div>
                <div>
                  <h3 className="tw-text-xl tw-font-bold tw-text-gray-900 tw-font-['Lexend'] tw-m-0">{t('AddCreditPage/savings-title')}</h3>
                  <p className="tw-text-sm tw-text-gray-500 tw-font-['Lexend'] tw-m-0">
                    {t('AddCreditPage/savings-desc', { sessions: selectedPackage.sessionAmount.toLocaleString() })}
                  </p>
                </div>
              </div>

              <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-4 tw-gap-4">
                {/* Current Plan Card */}
                <div className={`tw-rounded-xl tw-p-5 tw-border-2 ${PLAN_COLORS[currentPlanTier].border} ${PLAN_COLORS[currentPlanTier].bg}`}>
                  <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
                    <span className={`tw-text-xs tw-font-bold tw-font-['Lexend'] tw-uppercase ${PLAN_COLORS[currentPlanTier].text}`}>
                      {t('AddCreditPage/your-plan')}
                    </span>
                    <span
                      className={`tw-text-xs tw-px-2 tw-py-1 tw-rounded-full tw-font-semibold ${PLAN_COLORS[currentPlanTier].badge} ${PLAN_COLORS[currentPlanTier].text}`}
                    >
                      {PLAN_DISPLAY_NAMES[currentPlanTier]}
                    </span>
                  </div>
                  <div className="tw-text-3xl tw-font-bold tw-text-gray-800 tw-font-['Lexend']">${selectedPackage.price}</div>
                </div>

                {/* Higher Plan Cards */}
                {savingsComparison.map(comparison => (
                  <div
                    key={comparison.plan}
                    className={`tw-rounded-xl tw-p-5 tw-border-2 ${PLAN_COLORS[comparison.plan].border} ${PLAN_COLORS[comparison.plan].bg}`}
                  >
                    {/* Line 1: With plan [Plan Name] */}
                    <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                      <span className={`tw-text-sm tw-font-bold tw-font-['Lexend'] ${PLAN_COLORS[comparison.plan].text}`}>{t('AddCreditPage/with-plan')}</span>
                      <span
                        className={`tw-text-xs tw-px-2 tw-py-1 tw-rounded-full tw-font-semibold ${PLAN_COLORS[comparison.plan].badge} ${
                          PLAN_COLORS[comparison.plan].text
                        }`}
                      >
                        {PLAN_DISPLAY_NAMES[comparison.plan]}
                      </span>
                    </div>
                    {/* Line 2: get your X sessions for $Y */}
                    <div className="tw-text-gray-700 tw-font-['Lexend'] tw-mb-2">
                      <span className="tw-text-sm">{t('AddCreditPage/get-sessions-for', { sessions: selectedPackage.sessionAmount.toLocaleString() })}</span>
                      <span className="tw-text-xl tw-font-bold tw-ml-1">${comparison.price}</span>
                    </div>
                    {/* Line 3: Save XX% */}
                    <div>
                      <span className="tw-bg-green-100 tw-text-green-700 tw-font-bold tw-text-sm tw-px-2 tw-py-1 tw-rounded-lg tw-font-['Lexend']">
                        {t('AddCreditPage/save')} {comparison.savingsPercent}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Upgrade CTA */}
              <div className="tw-mt-6 tw-pt-6 tw-border-t tw-border-gray-100 tw-text-right">
                <LouiseButton color="blue" cta={t('AddCreditPage/upgrade-now')} ctaTarget="register" onClick={handleUpgradeClick} />
              </div>
            </div>
          )}

          {/* Purchase Summary & Button */}
          {selectedPackage && (
            <div className="tw-bg-white tw-rounded-2xl tw-shadow-xl tw-p-6 md:tw-p-8">
              <div className="tw-flex tw-flex-col md:tw-flex-row tw-items-center tw-justify-between tw-gap-6">
                {/* Summary */}
                <div className="tw-flex tw-items-center tw-gap-4">
                  <div className="tw-w-16 tw-h-16 tw-bg-gradient-to-br tw-from-amber-400 tw-to-orange-500 tw-rounded-2xl tw-flex tw-items-center tw-justify-center tw-shadow-lg">
                    <BiCoin className="tw-text-white" size={32} />
                  </div>
                  <div>
                    <p className="tw-text-sm tw-text-gray-500 tw-font-['Lexend'] tw-m-0">{t('AddCreditPage/selected-package')}</p>
                    <p className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-font-['Lexend'] tw-m-0">
                      {selectedPackage.sessionAmount.toLocaleString()} {t('AddCreditPage/l-05')}
                    </p>
                    <p className="tw-text-lg tw-font-semibold tw-text-blue-600 tw-font-['Lexend'] tw-m-0">
                      ${selectedPackage.price} {CURRENCY}
                    </p>
                  </div>
                </div>

                {/* Purchase Button */}
                <LouiseButton
                  color={selectedPackage ? 'blue' : 'gray-light'}
                  cta={t('AddCreditPage/btn-01')}
                  ctaTarget="other"
                  onClick={selectedPackage ? handlePurchase : undefined}
                  isLoading={isPaymentLoading}
                />
              </div>
            </div>
          )}

          {/* Trust Badges */}
          <div className="tw-mt-8 tw-flex tw-flex-wrap tw-justify-center tw-gap-6 tw-text-gray-400">
            <div className="tw-flex tw-items-center tw-gap-2">
              <FiCheck className="tw-text-green-500" size={18} />
              <span className="tw-text-sm tw-font-['Lexend']">{t('AddCreditPage/instant-delivery')}</span>
            </div>
            <div className="tw-flex tw-items-center tw-gap-2">
              <FiCheck className="tw-text-green-500" size={18} />
              <span className="tw-text-sm tw-font-['Lexend']">{t('AddCreditPage/no-expiry')}</span>
            </div>
          </div>
        </div>
      </section>
    );

  return <React.Fragment>{content}</React.Fragment>;
};

export default WrappedErrorBoundary(CreditCreatePage);
